import { useCallback, useEffect, useRef, useState } from "react";
import type { ClientOrder } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type MyOrdersState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; orders: ClientOrder[]; customerNumber: string | null };

export interface UseMyOrdersResult {
  state: MyOrdersState;
  refetch: () => void;
}

/* Shape copied from useCatalog.ts and useLabResults.ts — fetch on demand,
   no poll, no react-query.
 *
 * `enabled` rather than an unconditional fetch: the Store tab stays mounted
 * for the whole session (see TabPanels), so an eager fetch here would pull a
 * client's order history on every app boot to render a screen most of them
 * never open. It runs the first time the history is opened, and again on each
 * later open — an order placed since is the one thing that could have changed.
 */
export function useMyOrders(enabled: boolean): UseMyOrdersResult {
  const api = useApi();
  const [state, setState] = useState<MyOrdersState>({ status: "loading" });
  const cancelledRef = useRef(false);

  const fetchOrders = useCallback(() => {
    setState({ status: "loading" });
    api.commerce
      .listMyOrders()
      .then((data) => {
        if (!cancelledRef.current)
          setState({ status: "success", orders: data.orders, customerNumber: data.customerNumber });
      })
      .catch(() => {
        if (!cancelledRef.current) setState({ status: "error" });
      });
  }, [api]);

  useEffect(() => {
    if (!enabled) return;
    cancelledRef.current = false;
    fetchOrders();
    return () => {
      cancelledRef.current = true;
    };
  }, [enabled, fetchOrders]);

  return { state, refetch: fetchOrders };
}
